import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useChat, isChatLate, FUNNELS, funnelOf } from "@/components/chat/ChatProvider";
import { ChatList } from "@/components/chat/ChatList";
import { ChatThread } from "@/components/chat/ChatThread";
import { PresenceToggle } from "@/components/chat/PresenceToggle";
import { MessagesSquare, PanelRightClose, Maximize2, ChevronLeft } from "lucide-react";

const waitingNow = (s) => s.waiting_since || s.unread > 0;
const rank = (s) => (isChatLate(s) ? 0 : s.priority && waitingNow(s) ? 1 : waitingNow(s) ? 2 : 3);
export const FunnelFilter = ({ value, onChange }) => (
  <select data-testid="chat-funnel-filter" value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full rounded-lg border border-[#1F293D] bg-[#0B0F17] px-2 py-1.5 text-xs text-slate-200 outline-none">
    <option value="">Todos os funis</option>
    {Object.entries(FUNNELS).map(([k, f]) => <option key={k} value={k}>{f.label}</option>)}
  </select>
);
export const DOCK_KEY = "crm_chat_dock_open";

const Counter = ({ label, value, tone, testid }) => (
  <div data-testid={testid} className="flex-1 rounded-lg border border-[#1F293D] bg-[#0B0F17] px-2 py-1.5 text-center">
    <div className="font-mono text-lg font-bold" style={{ color: tone }}>{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
  </div>
);

export const ChatDock = ({ open, setOpen }) => {
  const { items, reload } = useChat();
  const [sid, setSid] = useState(null);
  const [fun, setFun] = useState("");
  const { pathname } = useLocation();
  if (pathname.startsWith("/chat")) return null;
  const sorted = items.filter((s) => !fun || funnelOf(s) === fun).sort((a, b) => rank(a) - rank(b) || (b.last_at || "").localeCompare(a.last_at || ""));
  const late = items.filter(isChatLate).length;
  const waiting = items.filter((s) => s.waiting_since || s.unread > 0).length;

  if (!open) {
    return (
      <button data-testid="chat-dock-open" onClick={() => setOpen(true)} title="Abrir chat"
        className={`fixed bottom-20 right-4 z-40 flex items-center gap-2 rounded-full px-4 py-3 text-sm font-bold shadow-xl transition-transform hover:scale-105 min-[1440px]:bottom-auto min-[1440px]:right-0 min-[1440px]:top-1/2 min-[1440px]:-translate-y-1/2 min-[1440px]:rounded-l-xl min-[1440px]:rounded-r-none min-[1440px]:px-2 min-[1440px]:py-4 min-[1440px]:[writing-mode:vertical-rl] ${late ? "animate-pulse bg-red-500 text-white" : "bg-sky-500 text-slate-950"}`}>
        <MessagesSquare className="h-4 w-4" /> Chat {waiting > 0 && <span data-testid="chat-dock-badge" className="rounded-full bg-black/25 px-1.5 text-xs">{waiting}</span>}
      </button>
    );
  }
  return (
    <aside data-testid="chat-dock" className="fixed inset-y-0 right-0 z-40 flex w-full max-w-[420px] flex-col border-l border-[#1F293D] bg-[#0F141F] shadow-2xl min-[1440px]:shadow-none">
      <div className="space-y-3 border-b border-[#1F293D] p-3">
        <div className="flex items-center gap-2">
          <MessagesSquare className="h-5 w-5 text-sky-400" />
          <span className="flex-1 font-semibold text-white">Chat do site</span>
          <Link to="/chat" data-testid="chat-dock-expand" title="Abrir em tela cheia" className="rounded-lg p-1.5 text-slate-400 hover:bg-[#1A2234] hover:text-white"><Maximize2 className="h-4 w-4" /></Link>
          <button data-testid="chat-dock-close" onClick={() => setOpen(false)} title="Recolher" className="rounded-lg p-1.5 text-slate-400 hover:bg-[#1A2234] hover:text-white"><PanelRightClose className="h-4 w-4" /></button>
        </div>
        <PresenceToggle />
        <FunnelFilter value={fun} onChange={setFun} />
        <div className="flex gap-2">
          <Counter testid="chat-dock-waiting" label="aguardando" value={waiting} tone="#FBBF24" />
          <Counter testid="chat-dock-late" label="+5 min" value={late} tone={late ? "#F87171" : "#64748B"} />
          <Counter testid="chat-dock-open-count" label="abertas" value={items.length} tone="#38BDF8" />
        </div>
      </div>
      {sid ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <button data-testid="chat-dock-back" onClick={() => { setSid(null); reload(); }} className="flex items-center gap-1 border-b border-[#1F293D] px-3 py-2 text-left text-xs font-semibold text-sky-300 hover:bg-[#1A2234]">
            <ChevronLeft className="h-4 w-4" /> Conversas
          </button>
          <ChatThread key={sid} sid={sid} onChanged={reload} onDeleted={() => { setSid(null); reload(); }} />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2">
          <ChatList items={sorted} active={null} onPick={setSid} empty="Nenhuma conversa aberta" />
        </div>
      )}
    </aside>
  );
};
